import { isSupabaseConfigured, requireSupabase, ensureValidSession } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import type { Patient, PatientListItem, CreatePatientInput, FamilyDoctor } from "@/types/patient";

export interface PatientCareLinkContext {
    linkId: string;
    residentId: string | null;
    residentName: string | null;
    facilityId: string | null;
    facilityName: string | null;
    linkedAt: string | null;
}

type PracticeAuthorizedCareLinkRow = {
    link_id?: string | null;
    patient_id?: string | null;
    resident_id?: string | null;
    resident_name?: string | null;
    facility_id?: string | null;
    facility_name?: string | null;
    linked_at?: string | null;
};

type CareLinkDirectionRow = Record<string, unknown>;

function findPracticeAuthorizedCareLink(
    rows: PracticeAuthorizedCareLinkRow[],
    patientId: string,
    activeLinkId: string | null,
    residentId: string | null,
): PracticeAuthorizedCareLinkRow | null {
    return rows.find((row) => {
        const rowLinkId = typeof row.link_id === "string" ? row.link_id : null;
        return Boolean(activeLinkId) && rowLinkId === activeLinkId;
    }) ?? rows.find((row) => {
        const rowResidentId = typeof row.resident_id === "string" ? row.resident_id : null;
        const rowPatientId = typeof row.patient_id === "string" ? row.patient_id : null;
        return rowPatientId === patientId && rowResidentId === residentId;
    }) ?? null;
}

function resolveCareFacilityIdFromLink(row: CareLinkDirectionRow): string | null {
    const sourceType = typeof row.source_type === "string" ? row.source_type : null;
    const targetType = typeof row.target_type === "string" ? row.target_type : null;

    if (sourceType === "care" && typeof row.source_facility_id === "string") {
        return row.source_facility_id;
    }

    if (targetType === "care" && typeof row.target_facility_id === "string") {
        return row.target_facility_id;
    }

    if (typeof row.source_facility_id === "string") {
        return row.source_facility_id;
    }

    if (typeof row.target_facility_id === "string") {
        return row.target_facility_id;
    }

    return null;
}

async function resolveCanonicalPracticeId(
    sb: ReturnType<typeof requireSupabase>,
    candidatePracticeId: string,
): Promise<string | null> {
    if (!candidatePracticeId) return null;

    // Attempt 1: practices.id = candidatePracticeId
    const byId = await sb
        .from("practices")
        .select("id")
        .eq("id", candidatePracticeId)
        .maybeSingle();

    if (byId.error) {
        logger.warn(
            "[resolveCanonicalPracticeId] practices.id Lookup Fehler:",
            byId.error.message,
            byId.error.code,
        );
    }
    if (byId.data?.id) return byId.data.id as string;

    // Attempt 2: practices.user_id = candidatePracticeId
    const byOwner = await sb
        .from("practices")
        .select("id")
        .eq("user_id", candidatePracticeId)
        .maybeSingle();

    if (byOwner.error) {
        logger.warn(
            "[resolveCanonicalPracticeId] practices.user_id Lookup Fehler:",
            byOwner.error.message,
            byOwner.error.code,
        );
    }
    if (byOwner.data?.id) return byOwner.data.id as string;

    logger.error("[resolveCanonicalPracticeId] Keine Praxis gefunden für:", candidatePracticeId);
    return null;
}

function extractJoinedPatientRow(
    row: { patients?: unknown } | null | undefined,
): Record<string, unknown> | null {
    if (!row?.patients) return null;

    if (Array.isArray(row.patients)) {
        const [patientRow] = row.patients;
        return patientRow && typeof patientRow === "object"
            ? (patientRow as Record<string, unknown>)
            : null;
    }

    return typeof row.patients === "object"
        ? (row.patients as Record<string, unknown>)
        : null;
}

function buildPatientNameSearchClause(rawQuery: string): string | null {
    const sanitizedQuery = rawQuery
        .trim()
        .toLowerCase()
        .replace(/[,.()"\\]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    if (!sanitizedQuery) return null;

    const searchTerms = Array.from(
        new Set([sanitizedQuery, ...sanitizedQuery.split(" ").filter((part) => part.length >= 2)]),
    );

    return searchTerms
        .flatMap((term) => [`first_name.ilike.%${term}%`, `last_name.ilike.%${term}%`])
        .join(",");
}

async function findPatientRowByReference(
    sb: ReturnType<typeof requireSupabase>,
    patientIdOrUserId: string,
): Promise<Record<string, unknown> | null> {
    const byRecordId = await sb
        .from("patients")
        .select("*")
        .eq("id", patientIdOrUserId)
        .maybeSingle();

    if (!byRecordId.error && byRecordId.data) {
        return byRecordId.data as Record<string, unknown>;
    }

    const byUserId = await sb
        .from("patients")
        .select("*")
        .eq("user_id", patientIdOrUserId)
        .maybeSingle();

    if (byUserId.error || !byUserId.data) return null;
    return byUserId.data as Record<string, unknown>;
}

async function findPracticePatientRowByReference(
    sb: ReturnType<typeof requireSupabase>,
    practiceId: string,
    patientIdOrUserId: string,
): Promise<Record<string, unknown> | null> {
    const byRecordId = await sb
        .from("patient_practices")
        .select("patient_id, patients!inner(*)")
        .eq("practice_id", practiceId)
        .eq("patient_id", patientIdOrUserId)
        .eq("is_active", true)
        .maybeSingle();

    const patientByRecordId = extractJoinedPatientRow(
        byRecordId.data as { patients?: unknown } | null | undefined,
    );
    if (!byRecordId.error && patientByRecordId) {
        return patientByRecordId;
    }

    const byUserId = await sb
        .from("patient_practices")
        .select("patient_id, patients!inner(*)")
        .eq("practice_id", practiceId)
        .eq("patients.user_id", patientIdOrUserId)
        .eq("is_active", true)
        .maybeSingle();

    if (byUserId.error || !byUserId.data) return null;
    return extractJoinedPatientRow(byUserId.data as { patients?: unknown } | null | undefined);
}

/**
 * Patient Service — List, search, CRUD for practice patients
 *
 * SCHEMA: patients hat KEIN practice_id!
 * Zuordnung läuft über Junction-Table `patient_practices`.
 * Aligned mit Mobile-App (practicePatientService.ts).
 */

export async function getPatients(
    practiceId: string,
    options?: {
        search?: string;
        insuranceType?: string;
        limit?: number;
        offset?: number;
        orderBy?: string;
        ascending?: boolean;
    },
): Promise<PatientListItem[]> {
    if (!isSupabaseConfigured()) return [];

    try {
        const sb = requireSupabase();
        const limit = options?.limit || 100;
        const offset = options?.offset || 0;

        // Ensure valid auth session before querying
        const token = await ensureValidSession();
        if (!token) {
            logger.error("[Patients] Keine gültige Auth-Session — Abfrage übersprungen");
            return [];
        }

        const canonicalPracticeId = await resolveCanonicalPracticeId(sb, practiceId);
        if (!canonicalPracticeId) {
            logger.error("[Patients] Keine gültige Praxis-ID auflösbar für:", practiceId);
            return [];
        }

        let query = sb
            .from("patient_practices")
            .select(
                `
                patient_id,
                relationship_status,
                last_appointment_at,
                connected_via,
                patients!inner (
                    id,
                    first_name,
                    last_name,
                    date_of_birth,
                    insurance_type,
                    insurance_name,
                    insurance_number,
                    phone,
                    email,
                    allergies,
                    avatar_url
                )
            `,
            )
            .eq("practice_id", canonicalPracticeId)
            .eq("is_active", true);

        if (options?.search) {
            const searchClause = buildPatientNameSearchClause(options.search);
            if (searchClause) {
                query = query.or(searchClause, {
                    foreignTable: "patients",
                });
            }
        }

        if (options?.insuranceType) {
            const dbInsuranceType = toDbInsuranceType(options.insuranceType);
            query = query.eq("patients.insurance_type", dbInsuranceType);
        }

        if (options?.orderBy) {
            if (options.orderBy === "last_name" || options.orderBy === "first_name") {
                query = query.order(options.orderBy, {
                    foreignTable: "patients",
                    ascending: options.ascending ?? true,
                });
            } else {
                query = query.order(options.orderBy, { ascending: options.ascending ?? true });
            }
        } else {
            query = query.order("last_appointment_at", { ascending: false, nullsFirst: false });
        }

        query = query.range(offset, offset + limit - 1);

        const { data, error } = await query;

        if (error) {
            logger.error(
                "[Patients] Laden fehlgeschlagen:",
                error.message,
                error.code,
                error.details,
            );

            // Bei Auth-Fehler: Session refreshen und einmal retry
            if (
                error.code === "PGRST301" ||
                error.message?.includes("JWT") ||
                error.code === "401"
            ) {
                logger.info("[Patients] Auth-Fehler erkannt, versuche Session-Refresh + Retry...");
                const freshToken = await ensureValidSession();
                if (freshToken) {
                    // Retry the query with fresh session
                    let retryQuery = sb
                        .from("patient_practices")
                        .select(
                            `
                            patient_id, relationship_status, last_appointment_at, connected_via,
                            patients!inner(id, first_name, last_name, date_of_birth, insurance_type,
                                insurance_name, insurance_number, phone, email, allergies, avatar_url)
                        `,
                        )
                        .eq("practice_id", canonicalPracticeId)
                        .eq("is_active", true)
                        .order("last_appointment_at", { ascending: false, nullsFirst: false })
                        .range(offset, offset + limit - 1);
                    const retryResult = await retryQuery;
                    if (!retryResult.error && retryResult.data) {
                        logger.info(
                            "[Patients] Retry erfolgreich:",
                            retryResult.data.length,
                            "Patienten",
                        );
                        return (retryResult.data || []).map((row: any) =>
                            mapPatientListItem({
                                ...row.patients,
                                last_appointment_at: row.last_appointment_at,
                            }),
                        );
                    }
                    logger.error(
                        "[Patients] Retry auch fehlgeschlagen:",
                        retryResult.error?.message,
                    );
                }
            }
            return [];
        }

        return (data || []).map((row: any) =>
            mapPatientListItem({
                ...row.patients,
                last_appointment_at: row.last_appointment_at,
            }),
        );
    } catch (err) {
        logger.error("[Patients] Laden fehlgeschlagen:", err);
        return [];
    }
}

export async function getPatientById(
    patientId: string,
    options?: { practiceId?: string },
): Promise<Patient | null> {
    const sb = requireSupabase();

    if (!patientId) return null;

    if (options?.practiceId) {
        const token = await ensureValidSession();
        if (!token) return null;

        const canonicalPracticeId = await resolveCanonicalPracticeId(sb, options.practiceId);
        if (!canonicalPracticeId) return null;

        const patientRow = await findPracticePatientRowByReference(
            sb,
            canonicalPracticeId,
            patientId,
        );
        if (!patientRow) return null;
        return mapPatient(patientRow);
    }

    const patientRow = await findPatientRowByReference(sb, patientId);
    if (!patientRow) return null;
    return mapPatient(patientRow);
}

export async function createPatient(
    practiceId: string,
    input: CreatePatientInput,
): Promise<Patient | null> {
    const sb = requireSupabase();

    try {
        // Ensure valid auth session before write operation
        const token = await ensureValidSession();
        if (!token) {
            throw new Error("Keine gültige Sitzung. Bitte erneut anmelden.");
        }

        const canonicalPracticeId = await resolveCanonicalPracticeId(sb, practiceId);
        if (!canonicalPracticeId) {
            throw new Error(
                `Praxis-ID konnte nicht aufgelöst werden (${practiceId.substring(0, 8)}…)`,
            );
        }

        // RPC: Patient + patient_practices in einer Transaktion
        const { data, error } = await sb.rpc("create_practice_patient", {
            p_practice_id: canonicalPracticeId,
            p_first_name: input.firstName,
            p_last_name: input.lastName,
            p_date_of_birth: input.dateOfBirth || null,
            p_email: input.email || null,
            p_phone: input.phone || null,
            p_insurance_type: toDbInsuranceType(input.insuranceType),
            p_insurance_name: input.insuranceProvider || input.insuranceName || null,
        });

        if (!error) {
            const row = Array.isArray(data) ? data[0] : data;
            if (row) {
                return mapPatient(row);
            }
        } else {
            logger.warn(
                "[Patients] RPC create_practice_patient Fehler, fallback auf Direkt-Insert:",
                error.message,
                error.code,
            );
        }

        // Fallback wie Mobile: direkt in patients + patient_practices
        const { data: patientRow, error: patientError } = await sb
            .from("patients")
            .insert({
                first_name: input.firstName,
                last_name: input.lastName,
                date_of_birth: input.dateOfBirth || null,
                email: input.email || null,
                phone: input.phone || null,
                insurance_type: toDbInsuranceType(input.insuranceType),
                insurance_name: input.insuranceProvider || input.insuranceName || null,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            })
            .select("*")
            .single();

        if (patientError || !patientRow?.id) {
            const detail = patientError?.message || "Keine Daten zurückgegeben";
            throw new Error(`Patient anlegen fehlgeschlagen: ${detail}`);
        }

        const { error: linkError } = await sb.from("patient_practices").insert({
            patient_id: patientRow.id,
            practice_id: canonicalPracticeId,
            is_active: true,
            connected_via: "manual",
            relationship_status: "active",
            created_at: new Date().toISOString(),
        });

        if (linkError) {
            logger.error(
                "[Patients] patient_practices Link fehlgeschlagen:",
                linkError.message,
                linkError.code,
            );
            // Patient was created but link failed — still return the patient
        }

        return mapPatient(patientRow);
    } catch (err) {
        logger.error("[Patients] Erstellen fehlgeschlagen:", err);
        throw err instanceof Error ? err : new Error(String(err));
    }
}

export async function updatePatient(
    patientId: string,
    updates: Partial<CreatePatientInput> & {
        gender?: string;
        street?: string;
        city?: string;
        postalCode?: string;
        insuranceNumber?: string;
    },
): Promise<boolean> {
    const sb = requireSupabase();
    const mapped: Record<string, unknown> = {};
    if (updates.firstName !== undefined) mapped.first_name = updates.firstName;
    if (updates.lastName !== undefined) mapped.last_name = updates.lastName;
    if (updates.dateOfBirth !== undefined) mapped.date_of_birth = updates.dateOfBirth;
    if (updates.email !== undefined) mapped.email = updates.email;
    if (updates.phone !== undefined) mapped.phone = updates.phone;
    if (updates.gender !== undefined) mapped.gender = updates.gender;
    if (updates.street !== undefined) mapped.street = updates.street;
    if (updates.city !== undefined) mapped.city = updates.city;
    if (updates.postalCode !== undefined) mapped.postal_code = updates.postalCode;
    if (updates.insuranceNumber !== undefined) mapped.insurance_number = updates.insuranceNumber;
    if (updates.insuranceType !== undefined)
        mapped.insurance_type = toDbInsuranceType(updates.insuranceType);
    if (updates.insuranceProvider !== undefined) mapped.insurance_name = updates.insuranceProvider;
    if (updates.insuranceName !== undefined) mapped.insurance_name = updates.insuranceName;

    const { error } = await sb.from("patients").update(mapped).eq("id", patientId);
    if (error) {
        logger.error("[Patients] Update fehlgeschlagen:", error);
        return false;
    }
    return true;
}

export async function getPatientCount(
    practiceId: string,
    options?: { search?: string; insuranceType?: string },
): Promise<number> {
    if (!isSupabaseConfigured()) return 0;
    try {
        const sb = requireSupabase();

        // Ensure valid auth session
        const token = await ensureValidSession();
        if (!token) {
            logger.error("[PatientCount] Keine gültige Auth-Session");
            return 0;
        }

        const canonicalPracticeId = await resolveCanonicalPracticeId(sb, practiceId);
        if (!canonicalPracticeId) {
            logger.error("[PatientCount] Keine gültige Praxis-ID auflösbar:", practiceId);
            return 0;
        }

        // Simpler count query: just count patient_practices rows (no embedded resource)
        // Avoids potential PostgREST issues with head:true + embedded resources
        if (options?.search || options?.insuranceType) {
            // With filters we need the embedded resource for patient field access
            const searchTerm = options?.search?.trim();
            let filteredQuery = sb
                .from("patient_practices")
                .select("patient_id, patients!inner(id, first_name, last_name, insurance_type)", {
                    count: "exact",
                    head: true,
                })
                .eq("practice_id", canonicalPracticeId)
                .eq("is_active", true);

            if (searchTerm) {
                const searchClause = buildPatientNameSearchClause(searchTerm);
                if (searchClause) {
                    filteredQuery = filteredQuery.or(searchClause, {
                        foreignTable: "patients",
                    });
                }
            }
            if (options?.insuranceType) {
                const dbInsuranceType = toDbInsuranceType(options.insuranceType);
                filteredQuery = filteredQuery.eq("patients.insurance_type", dbInsuranceType);
            }

            const { count, error } = await filteredQuery;
            if (error) {
                logger.error(
                    "[PatientCount] Filtered-Count fehlgeschlagen:",
                    error.message,
                    error.code,
                );
                return 0;
            }
            return count || 0;
        }

        // Simple count without embedded resource (no search/filter)
        const { count, error } = await sb
            .from("patient_practices")
            .select("patient_id", { count: "exact", head: true })
            .eq("practice_id", canonicalPracticeId)
            .eq("is_active", true);

        if (error) {
            logger.error("[PatientCount] Fehler:", error.message, error.code, error.details);
            return 0;
        }

        return count || 0;
    } catch (err) {
        logger.error("[PatientCount] Unerwarteter Fehler:", err);
        return 0;
    }
}

/** Delete a patient by ID */
export async function deletePatient(patientId: string): Promise<boolean> {
    const sb = requireSupabase();
    await ensureValidSession();
    const { error } = await sb.from("patients").delete().eq("id", patientId);
    if (error) {
        logger.error("[Patients] Löschen fehlgeschlagen:", error);
        return false;
    }
    return true;
}

export async function removePatientFromPractice(
    practiceId: string,
    patientId: string,
    reason?: string,
): Promise<void> {
    const sb = requireSupabase();

    const token = await ensureValidSession();
    if (!token) {
        throw new Error("Keine gültige Sitzung. Bitte erneut anmelden.");
    }

    const canonicalPracticeId = await resolveCanonicalPracticeId(sb, practiceId);
    if (!canonicalPracticeId) {
        throw new Error("Praxis-ID konnte nicht aufgelöst werden.");
    }

    const timestamp = new Date().toISOString();
    const { error } = await sb
        .from("patient_practices")
        .update({
            is_active: false,
            relationship_status: "inactive",
            removed_at: timestamp,
            removal_reason: reason || "Praxis-seitiges Entfernen (Web)",
            updated_at: timestamp,
        })
        .eq("practice_id", canonicalPracticeId)
        .eq("patient_id", patientId);

    if (error) {
        logger.error(
            "[Patients] Praxis-Entfernen fehlgeschlagen:",
            error.message,
            error.code,
            error.details,
        );
        throw new Error(`Patient konnte nicht aus der Praxis entfernt werden: ${error.message}`);
    }
}

/** Update patient notes field */
export async function updatePatientNotes(patientId: string, notes: string): Promise<boolean> {
    const sb = requireSupabase();
    await ensureValidSession();
    const { error } = await sb.from("patients").update({ notes }).eq("id", patientId);
    if (error) {
        logger.error("[Patients] Notiz-Update fehlgeschlagen:", error);
        return false;
    }
    return true;
}

/**
 * Search patients by name for autocomplete
 * Uses patient_practices junction, with fallback to direct query
 */
export async function searchPatients(practiceId: string, query: string): Promise<Patient[]> {
    if (!isSupabaseConfigured() || !query || query.length < 2) return [];

    const searchClause = buildPatientNameSearchClause(query);

    if (!searchClause) return [];

    try {
        const sb = requireSupabase();
        const canonicalPracticeId = await resolveCanonicalPracticeId(sb, practiceId);
        if (!canonicalPracticeId) {
            logger.error("[PatientSearch] Keine gültige Praxis-ID auflösbar:", practiceId);
            return [];
        }

        const { data, error } = await sb
            .from("patient_practices")
            .select(
                `
                patient_id,
                relationship_status,
                last_appointment_at,
                connected_via,
                patients!inner (
                    *
                )
            `,
            )
            .eq("practice_id", canonicalPracticeId)
            .eq("is_active", true)
            .or(searchClause, {
                foreignTable: "patients",
            })
            .order("last_name", { foreignTable: "patients", ascending: true })
            .limit(10);

        if (error) {
            logger.error("[PatientSearch] Fehler:", error.message, error.code);
        }

        if (data && data.length > 0) {
            return data.map((row: any) =>
                mapPatient({
                    ...row.patients,
                    last_visit_at: row.last_appointment_at,
                }),
            );
        }

        // Fallback: direkte Suche (z.B. wenn Patient noch nicht verknüpft)
        logger.warn(
            "[PatientSearch] Keine verknüpften Patienten gefunden, Fallback auf direkte Suche",
        );

        const { data: fallback, error: fallbackError } = await sb
            .from("patients")
            .select("*")
            .or(searchClause)
            .order("last_name", { ascending: true })
            .limit(10);

        if (fallbackError) {
            logger.error("[PatientSearch] Fallback-Fehler:", fallbackError.message);
        }

        return (fallback || []).map(mapPatient);
    } catch (err) {
        logger.error("[PatientSearch] Unerwarteter Fehler:", err);
        return [];
    }
}

export async function getActiveCareLinkContextForPatient(
    practiceId: string,
    patientId: string,
): Promise<PatientCareLinkContext | null> {
    if (!isSupabaseConfigured() || !practiceId || !patientId) return null;

    try {
        const sb = requireSupabase();
        const canonicalPracticeId = await resolveCanonicalPracticeId(sb, practiceId);
        if (!canonicalPracticeId) {
            logger.error("[PatientCareLink] Keine gültige Praxis-ID auflösbar:", practiceId);
            return null;
        }

        const { data, error } = await sb
            .from("cross_sector_data_sharing")
            .select(
                "id, resident_id, source_type, source_facility_id, target_type, target_facility_id, created_at, expires_at, status, is_active, source_practice_id, target_practice_id",
            )
            .eq("patient_id", patientId)
            .eq("is_active", true);

        if (error) {
            logger.error("[PatientCareLink] Laden fehlgeschlagen:", error.message, error.code);
            return null;
        }

        const now = Date.now();
        const activeLink = ((data ?? []) as Array<Record<string, unknown>>).find((row) => {
            const linkedPracticeId = typeof row.source_practice_id === "string"
                ? row.source_practice_id
                : typeof row.target_practice_id === "string"
                    ? row.target_practice_id
                    : null;

            if (linkedPracticeId !== canonicalPracticeId) {
                return false;
            }

            if (typeof row.status === "string" && row.status !== "active") {
                return false;
            }

            if (row.expires_at == null) {
                return true;
            }

            if (typeof row.expires_at !== "string") {
                return false;
            }

            const expiresAtTimestamp = Date.parse(row.expires_at);
            return Number.isNaN(expiresAtTimestamp) || expiresAtTimestamp > now;
        });

        if (!activeLink) {
            return null;
        }

        const residentId = typeof activeLink.resident_id === "string" ? activeLink.resident_id : null;
        const facilityId = resolveCareFacilityIdFromLink(activeLink);
        const activeLinkId = typeof activeLink.id === "string" ? activeLink.id : null;

        const { data: authorizedCareLinks, error: authorizedCareLinksError } = await sb.rpc(
            "get_practice_authorized_care_links",
            {
                p_patient_ids: [patientId],
            },
        );

        if (authorizedCareLinksError) {
            logger.warn(
                "[PatientCareLink] RPC-Fassade fehlgeschlagen:",
                authorizedCareLinksError.message,
                authorizedCareLinksError.code,
            );
        }

        const authorizedCareLink = findPracticeAuthorizedCareLink(
            (authorizedCareLinks ?? []) as PracticeAuthorizedCareLinkRow[],
            patientId,
            activeLinkId,
            residentId,
        );

        let residentName = typeof authorizedCareLink?.resident_name === "string"
            && authorizedCareLink.resident_name.trim().length > 0
            ? authorizedCareLink.resident_name.trim()
            : null;
        let facilityName = typeof authorizedCareLink?.facility_name === "string"
            && authorizedCareLink.facility_name.trim().length > 0
            ? authorizedCareLink.facility_name.trim()
            : null;

        return {
            linkId: activeLinkId ?? "",
            residentId,
            residentName,
            facilityId:
                typeof authorizedCareLink?.facility_id === "string"
                    ? authorizedCareLink.facility_id
                    : facilityId,
            facilityName,
            linkedAt:
                typeof authorizedCareLink?.linked_at === "string"
                    ? authorizedCareLink.linked_at
                    : typeof activeLink.created_at === "string"
                        ? activeLink.created_at
                        : null,
        };
    } catch (error) {
        logger.error("[PatientCareLink] Unerwarteter Fehler:", error);
        return null;
    }
}

// --- Helpers (aligned with iOS: DB-native GKV/PKV/SZ values) ---

/**
 * Map legacy/mixed insurance type values to canonical DB values.
 * DB constraint: CHECK (insurance_type IN ('GKV', 'PKV', 'SZ'))
 * iOS uses GKV/PKV/SZ directly — Web now matches.
 */
function normalizeInsuranceType(val: string | null | undefined): "GKV" | "PKV" | "SZ" | null {
    if (!val) return null;
    switch (val.toLowerCase()) {
        case "gkv":
        case "public":
        case "gesetzlich":
            return "GKV";
        case "pkv":
        case "private":
        case "privat":
            return "PKV";
        case "sz":
        case "self_pay":
        case "selbstzahler":
            return "SZ";
        default:
            return null;
    }
}

/** Read mapper: DB row → App type (identity for canonical values, normalize for legacy) */
function mapInsuranceType(dbVal: string | null | undefined): "GKV" | "PKV" | "SZ" | null {
    return normalizeInsuranceType(dbVal);
}

/** Parse JSONB hausarzt_info → FamilyDoctor (graceful) */
function parseFamilyDoctor(raw: unknown): FamilyDoctor | undefined {
    if (!raw || typeof raw !== "object") return undefined;
    const obj = raw as Record<string, unknown>;
    const name = typeof obj.name === "string" ? obj.name : undefined;
    if (!name) return undefined;
    return {
        name,
        fachrichtung: typeof obj.fachrichtung === "string" ? obj.fachrichtung : undefined,
        phone: typeof obj.phone === "string" ? obj.phone : undefined,
        praxisName: typeof obj.praxisName === "string" ? obj.praxisName : undefined,
    };
}

/** Write mapper: App value → DB value (same canonical values as iOS) */
function toDbInsuranceType(appVal: string | null | undefined): string {
    return normalizeInsuranceType(appVal) || "GKV";
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapPatientListItem(row: any): PatientListItem {
    return {
        id: row.id,
        firstName: row.first_name || "",
        lastName: row.last_name || "",
        dateOfBirth: row.date_of_birth || undefined,
        insuranceType: mapInsuranceType(row.insurance_type),
        insuranceName: row.insurance_name || undefined,
        phone: row.phone || undefined,
        email: row.email || undefined,
        lastVisit: row.last_visit || row.last_appointment_at || undefined,
        lastVisitAt: row.last_visit || row.last_appointment_at || undefined,
        avatarUrl: row.avatar_url || undefined,
    };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapPatient(row: any): Patient {
    return {
        id: row.id,
        userId: row.user_id || undefined,
        firstName: row.first_name || "",
        lastName: row.last_name || "",
        dateOfBirth: row.date_of_birth || undefined,
        gender: row.gender || undefined,
        email: row.email || undefined,
        phone: row.phone || undefined,
        street: row.street || undefined,
        city: row.city || undefined,
        postalCode: row.postal_code || undefined,
        insuranceType: mapInsuranceType(row.insurance_type),
        insuranceName: row.insurance_name || undefined,
        insuranceProvider: row.insurance_name || undefined,
        insuranceNumber: row.insurance_number || undefined,
        allergies: row.allergies || undefined,
        bloodType: row.blood_type || undefined,
        medications: row.current_medications || row.medications || undefined,
        pflegegrad: row.pflegegrad ?? null,
        hasPatientenverfuegung: row.has_patientenverfuegung ?? false,
        familienstand: row.familienstand ?? null,
        staatsangehoerigkeit: row.staatsangehoerigkeit ?? null,
        lastVisit: row.last_visit || row.last_visit_at || undefined,
        lastVisitAt: row.last_visit || row.last_visit_at || undefined,
        nextAppointment: row.next_appointment || undefined,
        avatarUrl: row.avatar_url || undefined,
        notes: row.notes || undefined,
        familyDoctor: parseFamilyDoctor(row.hausarzt_info),
        createdAt: row.created_at,
        updatedAt: row.updated_at || row.created_at,
    };
}
